import React, { useRef, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import FadeOutOverlay from './FadeOutOverlay';
import { cardBackground } from '../../styles/surfaces';

/**
 * ShowMoreContent - Animated expand/collapse content container
 *
 * A headless component that provides animated height transitions for
 * expandable/collapsible content. Extracted from ShowMoreCard for reuse
 * in contexts where a Card wrapper is not needed.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to display
 * @param {number|null} props.maxHeight - Maximum height when collapsed (null = no limit)
 * @param {boolean} props.expanded - Controlled expansion state
 * @param {Function} props.onToggle - Callback when expand/collapse is triggered
 * @param {Function} props.onAnimationComplete - Callback when animation completes, receives isExpanded
 * @param {Function} props.onMeasureComplete - Callback when content measurement completes, receives { needsTruncation, naturalHeight }
 * @param {number} props.fadeHeight - Height of fade overlay (default: 32)
 * @param {object} props.sx - Additional sx styles for container
 * @param {React.ComponentType} props.outerContainerComponent - Optional wrapper component that receives
 *   { needsTruncation, hasMeasured, expanded, onToggle, children } props to conditionally render
 *   different containers based on whether content needs truncation
 */
export default function ShowMoreContent({
  children,
  maxHeight = null,
  expanded: expandedControlled,
  onToggle,
  onAnimationComplete,
  onMeasureComplete,
  fadeHeight = 32,
  buttonComponent: ButtonComponent = Box,
  buttonProps = {},
  sx = {},
  outerContainerComponent: OuterContainer,
}) {
  const theme = useTheme();
  const isExpandedControlled = expandedControlled !== undefined;
  const [expanded, setExpanded] = useState(false);
  const isExpanded = isExpandedControlled ? expandedControlled : expanded;
  const { buttonSx } = buttonProps;

  const contentRef = useRef(null);
  const [naturalHeight, setNaturalHeight] = useState(0);
  const [hasMeasured, setHasMeasured] = useState(false);

  // Measure natural content height
  useEffect(() => {
    const measureHeight = () => {
      if (contentRef.current) {
        setNaturalHeight(contentRef.current.scrollHeight || 0);
        setHasMeasured(true);
      }
    };

    // Use double RAF for reliable measurement after render
    let rafId1;
    let rafId2;
    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(measureHeight);
    });

    // Also measure after a short delay for slow test environments
    const timeoutId = setTimeout(measureHeight, 100);

    const ro = new ResizeObserver(measureHeight);
    if (contentRef.current) ro.observe(contentRef.current);

    return () => {
      if (rafId1) cancelAnimationFrame(rafId1);
      if (rafId2) cancelAnimationFrame(rafId2);
      clearTimeout(timeoutId);
      ro.disconnect();
    };
  }, [children]);

  // Notify parent when measurement completes
  useEffect(() => {
    if (hasMeasured && onMeasureComplete) {
      const needsTruncation = maxHeight !== null && naturalHeight > maxHeight;
      onMeasureComplete({ needsTruncation, naturalHeight });
    }
  }, [hasMeasured, naturalHeight, maxHeight, onMeasureComplete]);

  const toggleExpansion = () => {
    if (onToggle) onToggle(!isExpanded);
    else setExpanded(!isExpanded);
  };

  // Show button if content exceeds maxHeight
  const showButton = maxHeight !== null && naturalHeight > maxHeight;

  // Calculate display height
  const displayHeight = (() => {
    if (!hasMeasured) {
      // Before measurement, use maxHeight to avoid animation from 'auto'
      return maxHeight !== null ? maxHeight : 'auto';
    }
    if (maxHeight === null) {
      // No height limit - show natural height
      return naturalHeight || 'auto';
    }
    if (isExpanded) {
      // Expanded - show natural height
      return naturalHeight + (showButton ? 28 : 0);
    }
    // Collapsed - show maxHeight (or natural if smaller)
    return Math.min(maxHeight, naturalHeight) || 'auto';
  })();

  // Build the inner content
  const innerContent = (
    <Box sx={{ position: 'relative', ...sx }}>
      {/* Hidden element to measure natural content height */}
      <Box
        ref={contentRef}
        aria-hidden="true"
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          right: 0,
          opacity: 0,
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        {children}
      </Box>

      {/* Animated content viewport */}
      <Box
        id="showMoreContent.content.viewport"
        component={motion.div}
        initial={false}
        animate={{ height: displayHeight }}
        transition={{
          duration: theme.transitions.duration.standard / 1000,
          ease: 'easeInOut',
        }}
        onAnimationComplete={() => {
          if (onAnimationComplete) onAnimationComplete(isExpanded);
        }}
        sx={(theme) => ({
          overflow: 'hidden',
          position: 'relative',
          '--fade-bg': cardBackground(theme),
        })}
      >
        <FadeOutOverlay
          enabled={showButton}
          isExpanded={isExpanded}
          fadeHeight={fadeHeight}
          clamp={false}
        >
          {children}
        </FadeOutOverlay>
      </Box>

      {/* Expand/collapse button */}
      {showButton && (
        <ButtonComponent
          {...buttonProps}
          onClick={toggleExpansion}
          role="button"
          aria-expanded={isExpanded}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleExpansion();
            }
          }}
          sx={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            left: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            zIndex: 1,
            minHeight: 28,
            cursor: 'pointer',
            ...buttonSx,
          }}
        >
          <Typography variant="body2" sx={{ color: 'primary.main' }}>
            ...
          </Typography>
        </ButtonComponent>
      )}
    </Box>
  );

  // If custom outer container provided, wrap with it and pass state
  if (OuterContainer) {
    return (
      <OuterContainer
        needsTruncation={showButton}
        hasMeasured={hasMeasured}
        expanded={isExpanded}
        onToggle={toggleExpansion}
      >
        {innerContent}
      </OuterContainer>
    );
  }

  // Default: just return inner content
  return innerContent;
}
